import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { fetchClientServices, type ClientServiceListItem } from '../widget-api/client-services';
import { fetchPublicTrainers, type PublicTrainerItem } from '../widget-api/public-trainers';
import { useAsync } from '../widget-hooks/useAsync';
import { useBranchLabels } from '../widget-hooks/useBranchLabels';
import { formatIsoDateDdMmYyyy } from '../widget-lib/format-date';
import { buildServiceOptionLabels } from '../widget-lib/service-option-labels';
import { hasWidgetAuthQuery, widgetAuthQueryFromSearchParams } from '../widget-lib/widget-auth-query';

export type ScheduleFiltersApplyPayload = {
  branchId: number | null;
  /** Режим «все салоны» (сеть, несколько филиалов). */
  allBranches: boolean;
  serviceId: number | null;
  trainerUserId: number | null;
  fromDate: string;
  toDate: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  branches: { id: number; name: string }[];
  /** Текущий branch_id из URL (null = не выбран при сети). */
  branchId: number | null;
  /** В URL задано all_branches=1. */
  allBranchesActive: boolean;
  serviceId: number | null;
  trainerUserId: number | null;
  fromDate: string;
  toDate: string;
  organizationId: number;
  effectiveBranchId: number | null;
  orgSlug: string | null;
  accessToken: string | null;
  canLoadServices: boolean;
  onApply: (payload: ScheduleFiltersApplyPayload) => void;
  /** Сброс: очистить услугу и даты в URL, филиал — первый в списке (сеть). */
  onReset: () => void;
};

export function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

type FormattedDateInputProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  className: string;
};

/**
 * Нативный input даты прозрачным слоем поверх детерминированной подписи «дд.мм.гггг»:
 * выбор даты остаётся системным, а отображение не зависит от локали WebView.
 */
function FormattedDateInput({ id, value, onChange, className }: FormattedDateInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayValue = formatIsoDateDdMmYyyy(value);

  function openNativePicker() {
    const input = inputRef.current;
    if (!input || typeof input.showPicker !== 'function') return;
    try {
      input.showPicker();
    } catch {
      // В ограниченных контекстах (например, iframe) остаётся штатное открытие по тапу.
    }
  }

  return (
    <div className="relative rounded-md focus-within:ring-2 focus-within:ring-[var(--focus-ring)]">
      <input
        ref={inputRef}
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={openNativePicker}
        className="absolute inset-0 z-[1] h-full w-full cursor-pointer opacity-0"
      />
      <div aria-hidden className={`${className} pointer-events-none flex items-center`}>
        <span className={displayValue ? undefined : 'text-[var(--text-muted)]'}>
          {displayValue || 'дд.мм.гггг'}
        </span>
      </div>
    </div>
  );
}

/**
 * Полноэкранный оверлей: фильтры расписания по салону, услуге и периоду дат.
 */
export function ScheduleFiltersModal({
  open,
  onClose,
  branches,
  branchId,
  allBranchesActive,
  serviceId,
  trainerUserId,
  fromDate,
  toDate,
  organizationId,
  effectiveBranchId,
  orgSlug,
  accessToken,
  canLoadServices,
  onApply,
  onReset
}: Props) {
  const [searchParams] = useSearchParams();
  const [localBranch, setLocalBranch] = useState<string>('');
  const [localService, setLocalService] = useState<string>('');
  const [localTrainer, setLocalTrainer] = useState<string>('');
  const [localFrom, setLocalFrom] = useState('');
  const [localTo, setLocalTo] = useState('');

  const branchLabels = useBranchLabels();
  const showBranchFilter = branches.length > 1;
  /** Тренеры доступны только с токеном, ключом/подписью или в глобальном виджете — иначе бэкенд вернёт 422. */
  const hasAuthOrKey = Boolean(
    accessToken || hasWidgetAuthQuery(searchParams) || searchParams.get('embed_global') === '1'
  );

  useEffect(() => {
    if (!open) return;
    if (showBranchFilter) {
      if (allBranchesActive) {
        setLocalBranch('all');
      } else {
        const def = branchId != null ? String(branchId) : String(branches[0]?.id ?? '');
        setLocalBranch(def);
      }
    } else {
      setLocalBranch('');
    }
    setLocalService(serviceId != null ? String(serviceId) : '');
    setLocalTrainer(trainerUserId != null ? String(trainerUserId) : '');
    setLocalFrom(fromDate);
    setLocalTo(toDate);
  }, [open, branchId, allBranchesActive, serviceId, trainerUserId, fromDate, toDate, showBranchFilter, branches]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const { data: services, loading: servicesLoading, error: servicesError } = useAsync(
    () => {
      if (!open || !canLoadServices || organizationId == null || organizationId <= 0) {
        return Promise.resolve([] as ClientServiceListItem[]);
      }
      return fetchClientServices({
        organizationId,
        branchId: effectiveBranchId,
        accessToken
      });
    },
    [open, canLoadServices, organizationId, effectiveBranchId, accessToken]
  );

  const { data: trainers, loading: trainersLoading, error: trainersError } = useAsync(
    () => {
      if (!open || !orgSlug || !hasAuthOrKey) {
        return Promise.resolve([] as PublicTrainerItem[]);
      }
      return fetchPublicTrainers(
        orgSlug,
        widgetAuthQueryFromSearchParams(searchParams),
        accessToken,
        effectiveBranchId
      );
    },
    [open, orgSlug, hasAuthOrKey, searchParams, accessToken, effectiveBranchId]
  );

  const serviceOptions = useMemo(() => services ?? [], [services]);
  const serviceOptionLabels = useMemo(() => buildServiceOptionLabels(serviceOptions), [serviceOptions]);

  function handleApply() {
    let branchPayload: number | null = null;
    let allBranches = false;
    if (branches.length > 1) {
      if (localBranch === 'all') {
        allBranches = true;
        branchPayload = null;
      } else {
        const n = Number.parseInt(localBranch, 10);
        branchPayload = Number.isFinite(n) && n > 0 ? n : branches[0]?.id ?? null;
      }
    } else {
      branchPayload = branches[0]?.id ?? null;
    }

    let sid: number | null = null;
    if (localService !== '') {
      const n = Number.parseInt(localService, 10);
      if (Number.isFinite(n) && n > 0) sid = n;
    }

    let tUserId: number | null = null;
    if (localTrainer !== '') {
      const n = Number.parseInt(localTrainer, 10);
      if (Number.isFinite(n) && n > 0) tUserId = n;
    }

    onApply({
      branchId: branchPayload,
      allBranches,
      serviceId: sid,
      trainerUserId: tUserId,
      fromDate: localFrom,
      toDate: localTo
    });
    onClose();
  }

  function handleReset() {
    onReset();
    if (showBranchFilter) {
      setLocalBranch('all');
    }
    setLocalService('');
    setLocalTrainer('');
    onClose();
  }

  if (!open) return null;

  const inputClass =
    'h-10 w-full rounded-md border border-[var(--border)] bg-[var(--bg-soft)] px-3 text-[13px] text-[var(--text)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-filters-title"
        className="max-h-[min(92vh,640px)] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-[1] flex items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--bg-soft)] text-[var(--accent)]">
              <FilterIcon className="h-[18px] w-[18px]" />
            </span>
            <h2 id="schedule-filters-title" className="text-lg font-semibold tracking-tight text-[var(--text)]">
              Фильтр
            </h2>
          </div>
          <button
            type="button"
            className="rounded-md px-2 py-1 text-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>

        <div className="space-y-5 px-4 py-5 sm:px-5">
          {showBranchFilter ? (
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-[var(--text)]" htmlFor="sf-branch">
                {branchLabels.singular}
              </label>
              <select
                id="sf-branch"
                className={inputClass}
                value={localBranch}
                onChange={(e) => setLocalBranch(e.target.value)}
              >
                <option value="all">Все {branchLabels.pluralLc}</option>
                {branches.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[var(--text-muted)]">
                «Все {branchLabels.pluralLc}» — расписание по всем {branchLabels.dativePluralLc} за период (в списке у
                слотов указана {branchLabels.singularLc}).
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <label className="text-[13px] font-medium text-[var(--text)]" htmlFor="sf-service">
              Услуга
            </label>
            {servicesLoading ? (
              <p className="text-sm text-[var(--text-muted)]">Загрузка услуг…</p>
            ) : servicesError ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">{servicesError}</p>
            ) : (
              <select
                id="sf-service"
                className={inputClass}
                value={localService}
                onChange={(e) => setLocalService(e.target.value)}
              >
                <option value="">— все услуги —</option>
                {serviceOptions.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {serviceOptionLabels.get(s.id) ?? s.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {hasAuthOrKey ? (
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-[var(--text)]" htmlFor="sf-trainer">
                Тренер
              </label>
              {trainersLoading ? (
                <p className="text-sm text-[var(--text-muted)]">Загрузка тренеров…</p>
              ) : trainersError ? (
                <p className="text-sm text-amber-700 dark:text-amber-400">{trainersError}</p>
              ) : (
                <select
                  id="sf-trainer"
                  className={inputClass}
                  value={localTrainer}
                  onChange={(e) => setLocalTrainer(e.target.value)}
                >
                  <option value="">— все тренеры —</option>
                  {trainers?.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.full_name || `Тренер #${t.id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-[var(--text)]" htmlFor="sf-from">
                Дата с
              </label>
              <FormattedDateInput
                id="sf-from"
                className={inputClass}
                value={localFrom}
                onChange={setLocalFrom}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-[var(--text)]" htmlFor="sf-to">
                Дата по
              </label>
              <FormattedDateInput
                id="sf-to"
                className={inputClass}
                value={localTo}
                onChange={setLocalTo}
              />
            </div>
          </div>
          <p className="text-xs text-[var(--text-muted)]">Период не более 60 дней.</p>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--border)] pt-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={handleReset}>
              Сбросить
            </Button>
            <Button type="button" onClick={handleApply}>
              Применить
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

